import { type VariantProps, cva } from 'class-variance-authority'

import { withPercentage } from '@/lib/string/string-utils'
import { cn } from '@/lib/ui/style-helpers'

const trackVariants = cva('w-full h-1 overflow-hidden bg-main-background-neutral', {
  variants: {
    rounded: {
      true: 'rounded-full',
      false: '',
    },
  },
  defaultVariants: {
    rounded: false,
  },
})

const indicatorVariants = cva('h-full transition-[width] duration-300', {
  variants: {
    rounded: {
      true: 'rounded-full',
      false: '',
    },
    color: {
      accent: 'bg-main-background-interactive-accent-default',
      success: 'bg-main-background-success',
      attention: 'bg-main-background-attention',
      danger: 'bg-main-background-danger',
    },
  },
  defaultVariants: {
    rounded: false,
    color: 'accent',
  },
})

type ProgressBarProps = VariantProps<typeof trackVariants> &
  VariantProps<typeof indicatorVariants> & {
    value: number
    'aria-label'?: string
  }

export const ProgressBar = ({
  value,
  rounded,
  color,
  'aria-label': ariaLabel,
}: ProgressBarProps) => {
  const clampedValue = Math.max(0, Math.min(100, value))

  return (
    <div
      role='progressbar'
      aria-label={ariaLabel}
      aria-valuenow={clampedValue}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(trackVariants({ rounded }))}
    >
      <div
        className={indicatorVariants({ rounded, color })}
        style={{ width: withPercentage(clampedValue) }}
      />
    </div>
  )
}
